/**
 * Centralized state cleanup utilities for client interactions
 * Handles cleanup of locked clients, flipped cards, and expanded rows
 */

import { useCallback } from 'react';

/**
 * Custom hook for managing interaction state cleanup
 * @param {Object} interactionStates - Current interaction states
 * @param {Object} interactionSetters - State setters for interactions
 * @param {Function} getClientKey - Function to generate client keys
 * @returns {Object} Cleanup utility functions
 */
const useInteractionCleanup = (interactionStates, interactionSetters, getClientKey) => {
  const {
    lockedClients,
    flippedCards,
    expandedTableRow
  } = interactionStates;

  const {
    setLockedClients,
    setFlippedCards,
    setExpandedTableRow
  } = interactionSetters;

  /**
   * Clean up all interaction states for a specific client
   * @param {Object} client - Client object to clean up
   */
  const cleanupClientInteractions = useCallback((client) => {
    const clientKey = getClientKey(client);
    
    // Remove from locked clients if present
    if (lockedClients.has(clientKey)) {
      const newLockedClients = new Set(lockedClients);
      newLockedClients.delete(clientKey);
      setLockedClients(newLockedClients);
    }

    // Remove from flipped cards if present
    if (flippedCards.has(clientKey)) {
      const newFlippedCards = new Set(flippedCards);
      newFlippedCards.delete(clientKey);
      setFlippedCards(newFlippedCards);
    }

    // Clear expanded row if it matches this client
    if (expandedTableRow === clientKey) {
      setExpandedTableRow(null);
    }
  }, [lockedClients, flippedCards, expandedTableRow, setLockedClients, setFlippedCards, setExpandedTableRow, getClientKey]);

  /**
   * Clean up interaction states for clients from a specific country
   * @param {Array} pinnedClients - Current pinned clients
   * @param {string} countryName - Country to clean up
   */
  const cleanupCountryInteractions = useCallback((pinnedClients, countryName) => {
    // Clean up locked clients from this country
    const newLockedClients = new Set();
    lockedClients.forEach(clientKey => {
      const matchingClient = pinnedClients.find(client => 
        getClientKey(client) === clientKey && client.country === countryName
      );
      if (!matchingClient) {
        newLockedClients.add(clientKey);
      }
    });
    setLockedClients(newLockedClients);
    
    // Clean up flipped cards from this country
    const newFlippedCards = new Set();
    flippedCards.forEach(clientKey => {
      const matchingClient = pinnedClients.find(client => 
        getClientKey(client) === clientKey && client.country === countryName
      );
      if (!matchingClient) {
        newFlippedCards.add(clientKey);
      }
    });
    setFlippedCards(newFlippedCards);
  }, [lockedClients, flippedCards, setLockedClients, setFlippedCards, getClientKey]);

  /**
   * Reset all interaction states
   */
  const resetAllInteractions = useCallback(() => {
    setFlippedCards(new Set());
    setExpandedTableRow(null);
    // Note: Don't clear locked clients - they should persist
  }, [setFlippedCards, setExpandedTableRow]);

  /**
   * Clean up interactions when view type changes
   */
  const cleanupOnViewChange = useCallback(() => {
    setExpandedTableRow(null);
    setFlippedCards(new Set());
  }, [setExpandedTableRow, setFlippedCards]);

  /**
   * Validate and clean up expanded row if client no longer exists
   * @param {Array} pinnedClients - Current pinned clients
   */
  const validateExpandedRow = useCallback((pinnedClients) => {
    if (expandedTableRow) {
      const isStillPinned = pinnedClients.some(client => getClientKey(client) === expandedTableRow);
      if (!isStillPinned) {
        setExpandedTableRow(null);
      }
    }
  }, [expandedTableRow, getClientKey, setExpandedTableRow]);

  return {
    cleanupClientInteractions,
    cleanupCountryInteractions,
    resetAllInteractions,
    cleanupOnViewChange,
    validateExpandedRow
  };
};

export default useInteractionCleanup;
